import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import './NewAcademyFeatures.css';
import { useNavigate, useLocation, Link } from "react-router-dom";
import Swal from 'sweetalert2';
import { useUser } from '../context/UserContext';

const NewAcademyFeatures = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { selectedCenter } = useUser();
    const searchParams = new URLSearchParams(location.search);
    const featureId = searchParams.get("featureId");
    const [academyOptions, setAcademyOptions] = useState([]);
    const [selectedAcademy, setSelectedAcademy] = useState(null);
    const [attractions, setAttractions] = useState([]);
    const [amenities, setAmenities] = useState([
        { id: null, tagline: '', icon: { fileName: '', preview: '' } }
    ]);
    const [rules, setRules] = useState([]);
    const [newRule, setNewRule] = useState('');
    const [timings, setTimings] = useState([
        { id: null, day: '', startTime: '', endTime: '' }
    ]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const daysOfWeek = [
        { value: 'MONDAY', label: 'Monday' },
        { value: 'TUESDAY', label: 'Tuesday' },
        { value: 'WEDNESDAY', label: 'Wednesday' },
        { value: 'THURSDAY', label: 'Thursday' },
        { value: 'FRIDAY', label: 'Friday' },
        { value: 'SATURDAY', label: 'Saturday' },
        { value: 'SUNDAY', label: 'Sunday' }
    ];

    const generateTimeOptions = () => {
        const times = [];
        for (let h = 0; h < 24; h++) {
            for (let m = 0; m < 60; m += 15) {
                const t = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                times.push({ value: t, label: t });
            }
        }
        return times;
    };

    const timeOptions = generateTimeOptions();

    const getEndTimeOptions = (startTime) => {
        if (!startTime) return timeOptions;

        return timeOptions.filter(o => {
            return o.value > startTime || o.value === '00:00';
        });
    };

    useEffect(() => {
        axios.get(process.env.REACT_APP_API_FEATURE_ACADEMY, {
            headers: {
                'Content-Type': 'application/json',
                'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
            }
        })
            .then(res => {
                const { academyMap = {} } = res.data.data;
                const options = Object.entries(academyMap).map(([id, name]) => ({
                    value: Number(id),
                    label: name,
                }));
                setAcademyOptions(options);
            })
            .catch(err => console.error('Error fetching academies:', err));
    }, []);

    useEffect(() => {
        if (selectedCenter && academyOptions.length > 0) {
            const match = academyOptions.find(opt => opt.value === Number(selectedCenter));
            if (match) {
                setSelectedAcademy(match);
            } else {
                console.warn('No academy option matches selectedCenter', selectedCenter);
            }
        }
    }, [selectedCenter, academyOptions]);

    useEffect(() => {
        if (!featureId) return;
        axios.get(
            `${process.env.REACT_APP_API_ADMIN_ACADEMY_FEATURE_LIST}?academyId=${featureId}`,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
                }
            }
        )
            .then(res => {
                const data = res.data.data;
                const academyData = data[`academy_${featureId}`] || Object.values(data)[0];

                if (!academyData) {
                    throw new Error(`No data found for academy_${featureId}`);
                }

                setSelectedAcademy({ value: academyData.academyId, label: academyData.academyName });

                setAttractions(
                    (academyData.attractions || []).map(a => ({
                        id: a.attractionId,
                        tagline: a.name || '',
                        icon: {
                            fileName: a.iconName || '',
                            preview: a.iconName || '',
                            isExisting: !!a.iconName
                        }
                    }))
                );

                setAmenities(
                    (academyData.amenities || []).map(am => ({
                        id: am.amenityId,
                        tagline: am.name || '',
                        icon: {
                            fileName: am.iconName || '',
                            preview: am.iconName || '',
                            isExisting: !!am.iconName
                        }
                    }))
                );

                setRules(
                    (academyData.rules || []).map(r => ({ id: r.ruleId, name: r.name }))
                );

                if (academyData.timings?.length) {
                    setTimings(academyData.timings.map(t => ({
                        id: t.timingId,
                        day: t.day,
                        startTime: t.startTime.slice(11, 16),
                        endTime: t.closeTime.slice(11, 16)
                    })));
                }
            })
            .catch(err => {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load academy details.',
                    toast: true, position: 'top-end',
                    showConfirmButton: false, timer: 3000
                });
            });
    }, [featureId]);

    const handleAddRow = type => {
        const blank = { id: null, tagline: '', icon: { fileName: '', preview: '', isExisting: false } };
        type === 'attraction'
            ? setAttractions(a => [...a, blank])
            : setAmenities(a => [...a, blank]);
    };

    const handleDeleteRow = (type, idx) => {
        if (type === 'attraction') {
            if (attractions.length > 1) setAttractions(a => a.filter((_, i) => i !== idx));
        } else {
            if (amenities.length > 1) setAmenities(a => a.filter((_, i) => i !== idx));
        }
    };

    const handleChange = (type, idx, field, val) => {
        const list = type === 'attraction' ? [...attractions] : [...amenities];
        list[idx][field] = val;
        type === 'attraction' ? setAttractions(list) : setAmenities(list);
    };

    const handleFileUpload = (e, type, idx) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const list = type === 'attraction' ? [...attractions] : [...amenities];
            list[idx] = {
                ...list[idx],
                icon: {
                    file: file,
                    fileName: file.name,
                    preview: reader.result,
                    isExisting: false
                }
            };
            type === 'attraction' ? setAttractions(list) : setAmenities(list);
        };
        reader.readAsDataURL(file);
    };

    const handleAddRule = () => {
        if (!newRule.trim()) return;
        setRules(r => [...r, { id: null, name: newRule.trim() }]);
        setNewRule('');
    };

    const handleDeleteRule = idx => {
        setRules(r => r.filter((_, i) => i !== idx));
    };

    const handleAddTiming = () => {
        setTimings([...timings, { id: null, day: '', startTime: '', endTime: '' }]);
    };

    const handleDeleteTiming = (idx) => {
        if (timings.length > 1) {
            setTimings(timings.filter((_, i) => i !== idx));
        }
    };

    const handleTimingChange = (idx, field, value) => {
        const newTimings = [...timings];
        newTimings[idx][field] = value;

        if (field === 'startTime') {
            newTimings[idx].endTime = '';
        }

        setTimings(newTimings);
    };

    const toMinutes = hhmm => {
        const [h, m] = hhmm.split(':').map(Number);
        return h * 60 + m;
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setIsSubmitting(true);

        if (!selectedAcademy?.value) {
            Swal.fire({
                icon: 'error',
                title: 'Oops!',
                text: 'Please select an academy first.',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            setIsSubmitting(false);
            return;
        }
        for (const a of attractions) {
            if (!a.tagline && !(a.icon && a.icon.file)) continue;

            if (!a.tagline) {
                Swal.fire({
                    icon: 'error',
                    title: 'Attraction name?',
                    text: 'Please give your attraction a name.',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000
                });
                setIsSubmitting(false);
                return;
            }
            if (!a.id && !(a.icon && a.icon.file)) {
                Swal.fire({
                    icon: 'error',
                    title: 'Icon missing',
                    text: `Please upload an icon for "${a.tagline}".`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000
                });
                setIsSubmitting(false);
                return;
            }
        }

        for (const t of timings) {
            const hasStart = typeof t.startTime === 'string' && t.startTime.trim() !== '';
            const hasEnd = typeof t.endTime === 'string' && t.endTime.trim() !== '';

            if (t.day && (!hasStart || !hasEnd)) {
                Swal.fire({
                    icon: 'error',
                    title: 'Missing time',
                    text: `Please select both start and end time for ${t.day}.`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000
                });
                setIsSubmitting(false);
                return;
            }
        }

        const byDay = timings.reduce((acc, t) => {
            if (t.day) {
                acc[t.day] = acc[t.day] || [];
                acc[t.day].push(t);
            }
            return acc;
        }, {});

        for (const [day, slots] of Object.entries(byDay)) {
            const sorted = slots
                .map(s => ({
                    ...s,
                    startMin: toMinutes(s.startTime),
                    endMin: toMinutes(s.endTime)
                }))
                .sort((a, b) => a.startMin - b.startMin);

            for (let i = 1; i < sorted.length; i++) {
                if (sorted[i].startMin < sorted[i - 1].endMin) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Overlap!',
                        text: `Your ${day} slots overlap. Please adjust.`,
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000
                    });
                    setIsSubmitting(false);
                    return;
                }
            }
        }

        if (amenities.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'Hold up!',
                text: 'You need at least one amenity.',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            setIsSubmitting(false);
            return;
        }
        for (const a of amenities) {
            if (!a.tagline) {
                Swal.fire({
                    icon: 'error',
                    title: 'Amenity name?',
                    text: 'Please give your amenity a name.',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000
                });
                setIsSubmitting(false);
                return;
            }
            if (!a.id && !(a.icon && a.icon.file)) {
                Swal.fire({
                    icon: 'error',
                    title: 'Icon missing',
                    text: `Please upload an icon for "${a.tagline}".`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000
                });
                setIsSubmitting(false);
                return;
            }
        }

        const getDateAdjustedTime = (timeStr, isEndTime = false) => {
            const [hours, minutes] = timeStr.split(':').map(Number);

            const date = new Date();
            date.setUTCHours(hours, minutes, 0, 0);

            if (isEndTime && hours === 0 && minutes === 0) {
                date.setUTCDate(date.getUTCDate() + 1);
            }

            return date.toISOString();
        };

        try {
            const timingsWithISO = timings.map(t => ({
                ...(t.id && { timingId: t.id }),
                day: t.day,
                startTime: getDateAdjustedTime(t.startTime, false),
                closeTime: getDateAdjustedTime(t.endTime, true)
            }));

            const jsonPayload = {
                academyId: selectedAcademy.value,
                amenities: amenities.map(a => ({
                    ...(a.id && { amenityId: a.id }),
                    name: a.tagline,
                    ...(a.icon.isExisting ? { icon: null, iconName: a.icon.preview } : {})
                })),
                attractions: attractions.map(a => ({
                    ...(a.id && { attractionId: a.id }),
                    name: a.tagline,
                    ...(a.icon.isExisting ? { icon: null, iconName: a.icon.preview } : {})
                })),
                rules: rules.map(r => ({
                    ...(r.id && { ruleId: r.id }),
                    name: r.name
                })),
                timings: timingsWithISO
            };
            const formData = new FormData();
            formData.append(
                'form',
                new Blob([JSON.stringify(jsonPayload)], { type: 'application/json' })
            );
            amenities.forEach(a => {
                if (a.icon.file && !a.icon.isExisting) {
                    formData.append('amenityIcons', a.icon.file);
                }
            });
            attractions.forEach(a => {
                if (a.icon.file && !a.icon.isExisting) {
                    formData.append('attractionIcons', a.icon.file);
                }
            });
            const response = await axios.post(
                process.env.REACT_APP_API_FEATURE_CREATE,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'x-authorization': process.env.REACT_APP_AUTH_TOKEN
                    }
                }
            );

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: featureId
                    ? 'Academy features updated successfully!'
                    : 'Academy features created successfully!',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            navigate('/academyFeatures');
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || error.message || 'Failed to save academy features';
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMessage,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="enrollment-container">
            <div className="breadcrumb">
                <Link to="/academyFeatures" className="breadcrumb-link">
                    Academy Features
                </Link> &gt; {featureId ? 'Edit Features' : 'New Features'}
            </div>
            <form onSubmit={handleSubmit}>
                
                <div className="floating-select-form" style={{ width: '98%' }}>
                    <Select
                        name="academy"
                        options={academyOptions}
                        value={selectedAcademy}
                        onChange={setSelectedAcademy}
                        className={`walkin-form-dropdown ${selectedAcademy ? "has-value" : ""}`}
                        classNamePrefix="naf-select"
                        placeholder="Choose Academy"
                        isClearable
                        styles={{
                            control: (provided) => ({
                                ...provided,
                                padding: '5px',
                            }),
                            placeholder: (provided) => ({
                                ...provided,
                                color: 'black',
                            }),
                        }}
                        menuPortalTarget={document.body}
                    />
                    {selectedAcademy && <label>Academy</label>}
                </div>

                <fieldset className="enrollment-form-fieldset enrollment-form-mt-3">
                    <legend className="enrollment-form-legend">Timings</legend>
                    <div className="rental-table-scroll">
                        <table className="rental-entry-table">
                            <thead>
                                <tr>
                                    <th>Day</th>
                                    <th>Start Time</th>
                                    <th>End Time</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {timings.map((timing, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <Select
                                                options={daysOfWeek}
                                                value={daysOfWeek.find(d => d.value === timing.day)}
                                                onChange={(selected) =>
                                                    handleTimingChange(idx, 'day', selected?.value || '')
                                                }
                                                placeholder="Select Day"
                                                className="walkin-form-dropdown"
                                                classNamePrefix="walkin-select"
                                                isClearable={false}
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        padding: '5px',
                                                        minWidth: '120px'
                                                    }),
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <Select
                                                options={timeOptions}
                                                value={timeOptions.find(t => t.value === timing.startTime)}
                                                onChange={(selected) =>
                                                    handleTimingChange(idx, 'startTime', selected?.value || '')
                                                }
                                                placeholder="Start Time"
                                                className="walkin-form-dropdown"
                                                classNamePrefix="walkin-select"
                                                isClearable={false}
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        padding: '5px',
                                                        minWidth: '120px'
                                                    }),
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <Select
                                                options={getEndTimeOptions(timing.startTime)}
                                                value={timeOptions.find(t => t.value === timing.endTime)}
                                                onChange={(selected) =>
                                                    handleTimingChange(idx, 'endTime', selected?.value || '')
                                                }
                                                placeholder="End Time"
                                                className="walkin-form-dropdown"
                                                classNamePrefix="walkin-select"
                                                isClearable={false}
                                                isDisabled={!timing.startTime}
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        padding: '5px',
                                                        minWidth: '120px'
                                                    }),
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                disabled={timings.length === 1}
                                                onClick={() => handleDeleteTiming(idx)}
                                                className="enrollment-form-button enrollment-form-button-danger"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <button
                        type="button"
                        className="plusButton"
                        onClick={handleAddTiming}
                    >
                        <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                            <g mask="url(#mask0_21_345)">
                                <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
                            </g>
                        </svg>
                    </button>
                </fieldset>

                <fieldset className="enrollment-form-fieldset enrollment-form-mt-3">
                    <legend className="enrollment-form-legend">Attractions</legend>
                    <div className="rental-table-scroll">
                        <table className="rental-entry-table">
                            <thead><tr>
                                <th>Tagline</th><th>Icon</th><th>Preview</th><th>Action</th>
                            </tr></thead>
                            <tbody>
                                {attractions.map((a, i) => (
                                    <tr key={a.id ?? i}>
                                        <td>
                                            <input
                                                className="rental-input"
                                                value={a.tagline}
                                                onChange={e => handleChange('attraction', i, 'tagline', e.target.value)}
                                                placeholder="Enter Tagline"
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                style={{ display: 'none' }}
                                                id={`attraction-icon-${i}`}
                                                onChange={e => handleFileUpload(e, 'attraction', i)}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => document.getElementById(`attraction-icon-${i}`).click()}
                                                style={{ padding: '4px 10px', background: '#eaf4ff', border: 'none' }}
                                            > <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                width="20"
                                                height="20"
                                                id="upload-image"
                                            >
                                                    <path
                                                        fill="black"
                                                        d="M19,13a1,1,0,0,0-1,1v.38L16.52,12.9a2.79,2.79,0,0,0-3.93,0l-.7.7L9.41,11.12a2.85,2.85,0,0,0-3.93,0L4,12.6V7A1,1,0,0,1,5,6h7a1,1,0,0,0,0-2H5A3,3,0,0,0,2,7V19a3,3,0,0,0,3,3H17a3,3,0,0,0,3-3V14A1,1,0,0,0,19,13ZM5,20a1,1,0,0,1-1-1V15.43l2.9-2.9a.79.79,0,0,1,1.09,0l3.17,3.17,0,0L15.46,20Zm13-1a.89.89,0,0,1-.18.53L13.31,15l.7-.7a.77.77,0,0,1,1.1,0L18,17.21ZM22.71,4.29l-3-3a1,1,0,0,0-.33-.21,1,1,0,0,0-.76,0,1,1,0,0,0-.33.21l-3,3a1,1,0,0,0,1.42,1.42L18,4.41V10a1,1,0,0,0,2,0V4.41l1.29,1.3a1,1,0,0,0,1.42,0A1,1,0,0,0,22.71,4.29Z"
                                                    />
                                                </svg></button>

                                        </td>
                                        <td>
                                            {a.icon.preview && (
                                                <img
                                                    src={a.icon.preview}
                                                    alt=""
                                                    style={{ width: 40, height: 40 }}
                                                    onError={(e) => {
                                                        e.target.src = 'https://via.placeholder.com/40';
                                                    }}
                                                />
                                            )}
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteRow('attraction', i)}
                                                className="enrollment-form-button enrollment-form-button-danger"
                                            ><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                                                </svg></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <button type="button" className="plusButton" onClick={() => handleAddRow('attraction')}>
                        <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                            <g mask="url(#mask0_21_345)">
                                <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
                            </g>
                        </svg>
                    </button>
                </fieldset>

                <div style={{ overflow: 'scroll' }}>
                    <fieldset className="enrollment-form-fieldset enrollment-form-mt-3">
                        <legend className="enrollment-form-legend">Amenities</legend>
                        <div className="rental-table-scroll">
                            <table className="rental-entry-table">
                                <thead><tr>
                                    <th>Tagline</th><th>Icon</th><th>Preview</th><th>Action</th>
                                </tr></thead>
                                <tbody>
                                    {amenities.map((am, i) => (
                                        <tr key={am.id ?? i}>
                                            <td>
                                                <input
                                                    className="rental-input"
                                                    value={am.tagline}
                                                    onChange={e => handleChange('amenity', i, 'tagline', e.target.value)}
                                                    placeholder="Enter Tagline"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    id={`amenity-icon-${i}`}
                                                    onChange={e => handleFileUpload(e, 'amenity', i)}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => document.getElementById(`amenity-icon-${i}`).click()}
                                                    style={{ padding: '4px 10px', background: '#eaf4ff', border: 'none' }}
                                                > <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    width="20"
                                                    height="20"
                                                    id="upload-image"
                                                >
                                                        <path
                                                            fill="black"
                                                            d="M19,13a1,1,0,0,0-1,1v.38L16.52,12.9a2.79,2.79,0,0,0-3.93,0l-.7.7L9.41,11.12a2.85,2.85,0,0,0-3.93,0L4,12.6V7A1,1,0,0,1,5,6h7a1,1,0,0,0,0-2H5A3,3,0,0,0,2,7V19a3,3,0,0,0,3,3H17a3,3,0,0,0,3-3V14A1,1,0,0,0,19,13ZM5,20a1,1,0,0,1-1-1V15.43l2.9-2.9a.79.79,0,0,1,1.09,0l3.17,3.17,0,0L15.46,20Zm13-1a.89.89,0,0,1-.18.53L13.31,15l.7-.7a.77.77,0,0,1,1.1,0L18,17.21ZM22.71,4.29l-3-3a1,1,0,0,0-.33-.21,1,1,0,0,0-.76,0,1,1,0,0,0-.33.21l-3,3a1,1,0,0,0,1.42,1.42L18,4.41V10a1,1,0,0,0,2,0V4.41l1.29,1.3a1,1,0,0,0,1.42,0A1,1,0,0,0,22.71,4.29Z"
                                                        />
                                                    </svg></button>

                                            </td>
                                            <td>
                                                {am.icon.preview && (
                                                    <img
                                                        src={am.icon.preview}
                                                        alt=""
                                                        style={{ width: 40, height: 40 }}
                                                        onError={(e) => {
                                                            e.target.src = 'https://via.placeholder.com/40';
                                                        }}
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    disabled={amenities.length === 1}
                                                    onClick={() => handleDeleteRow('amenity', i)}
                                                    className="enrollment-form-button enrollment-form-button-danger"
                                                > <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                                                    </svg></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <button type="button" className="plusButton" onClick={() => handleAddRow('amenity')}>
                            <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                                <g mask="url(#mask0_21_345)">
                                    <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
                                </g>
                            </svg>
                        </button>
                    </fieldset>
                </div>

                <div style={{ overflow: 'scroll' }}>
                    <fieldset className="enrollment-form-fieldset enrollment-form-mt-3">
                        <legend className="enrollment-form-legend">Rules</legend>
                        <div className="academy-rule-input-group-container">
                            <input
                                className="rental-input"
                                type="text"
                                value={newRule}
                                onChange={e => setNewRule(e.target.value)}
                                placeholder="Enter a rule"
                            />
                            <button
                                type="button"
                                className="plusButton"
                                onClick={handleAddRule}
                            >  <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                                    <g mask="url(#mask0_21_345)">
                                        <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
                                    </g>
                                </svg></button>
                        </div>
                        <ul className="academy-rules-display-list">
                            {rules.map((r, idx) => (
                                <li key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>{r.name}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteRule(idx)}
                                        style={{ marginLeft: '10px', padding: '4px 8px', fontSize: '12px', background: 'white', border: 'none' }}
                                    > <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="red" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                                        </svg></button>
                                </li>
                            ))}
                        </ul>
                    </fieldset>
                </div>

                <button
                    type="submit"
                    className="submit-button"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Saving...' : featureId ? 'Update' : 'Submit'}
                </button>
            </form>
        </div>
    );
};

export default NewAcademyFeatures;